import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDeliveryPartner } from "../../../features/dashboard/newBuilderSlice";

const DeliveryPartnerDropdown = ({ onSelect, limit, page, setPage }) => {
  const dispatch = useDispatch();
  const { deliveryPartners, loading } = useSelector(
    (state) => state.dashboard || {}
  );

  const totalPages = deliveryPartners?.pagination?.total_pages;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedPartner, setSelectedPartner] = useState(null); // ✅ Track selected partner

  const [mergedPartners, setMergedPartners] = useState([]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDeliveryPartner({ page, limit }));
    }
  }, [dispatch, page, limit, isOpen]);

  useEffect(() => {
    if (deliveryPartners?.data?.length) {
      setMergedPartners((prev) => {
        const currentPage = deliveryPartners.pagination.page;
        if (currentPage === 1) return deliveryPartners.data;

        const existingIds = new Set(prev.map((p) => p.id));
        const newItems = deliveryPartners.data.filter(
          (p) => !existingIds.has(p.id)
        );

        return currentPage > 1
          ? [...prev, ...newItems]
          : [...newItems, ...prev];
      });
    }
  }, [deliveryPartners]);

  // Infinite scroll up/down
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const dropdown = dropdownRef.current;
    let isFetching = false;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = dropdown;

      // Down → load next
      if (
        scrollHeight - scrollTop <= clientHeight + 20 &&
        !loading &&
        page < totalPages &&
        !isFetching
      ) {
        isFetching = true;
        setPage((prev) => prev + 1);
        setTimeout(() => (isFetching = false), 500);
      }

      // Up → load previous
      if (scrollTop <= 20 && !loading && page > 1 && !isFetching) {
        isFetching = true;
        setPage((prev) => prev - 1);
        setTimeout(() => (isFetching = false), 500);
      }
    };

    dropdown.addEventListener("scroll", handleScroll);
    return () => dropdown.removeEventListener("scroll", handleScroll);
  }, [loading, isOpen, page, totalPages]);

  return (
    <div className="dropdown-container" style={{ position: "relative" }}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="d-flex justify-content-between align-items-center"
        style={{
          width: "100%",
          padding: "6px 12px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          background: "#fff",
          textAlign: "left",
        }}
        type="button"
      >
        <span>
          {selectedPartner ? selectedPartner.name : "Select Delivery Partner"}
        </span>
        <i
          className="bi bi-caret-down-fill ms-2"
          style={{
            fontSize: "0.9rem",
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        ></i>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: "250px",
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "6px",
            zIndex: 10,
          }}
        >
          {mergedPartners.map((partner) => (
            <div
              key={partner.id}
              onClick={() => {
                setSelectedPartner(partner); // ✅ update button text
                onSelect(partner); // ✅ send to parent
                setIsOpen(false);
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #f1f1f1",
                background:
                  selectedPartner?.id === partner.id ? "#f8f9fa" : "#fff",
              }}
            >
              {partner.name}
            </div>
          ))}

          {loading && (
            <div
              style={{ textAlign: "center", padding: "10px", color: "#888" }}
            >
              Loading...
            </div>
          )}

          {!loading && page >= totalPages && (
            <div
              style={{
                textAlign: "center",
                padding: "10px",
                color: "#999",
                fontStyle: "italic",
              }}
            >
              All partners loaded
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveryPartnerDropdown;
